import { TaInput } from '@components/shared';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { decodeToken } from '@config/router/guards';
import { AuthHeader } from '@features/auth/AuthHeader';
import { PasswordRequirements } from '@features/auth/login';
import { passwordSchema, resetValues, ResetValues } from '@features/auth/registration';
import { AuthPepper } from '@features/AuthPaper';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { FormHelperText } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { RoleEnum } from '@utils/util.types';
import { useFormik } from 'formik';
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogin, useResetPasswordWithToken, useValidateResetPasswordToken } from 'services/auth';
import { useCheckUserCompletedRegistration } from 'services/users/usersHook';
import { mutate } from 'swr';

export const ResetPassword = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const { resetPasswordWithToken } = useResetPasswordWithToken();
	const { login } = useLogin();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const { checkStatus } = useCheckUserCompletedRegistration();
	const {
		validateToken,
		isLoading: isValidating,
		isValid,
		error: validationError,
	} = useValidateResetPasswordToken();
	const [resetSuccess, setResetSuccess] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const handleNavigation = (path: string) => {
		navigateWithLanguage(path);
	};

	const token = new URLSearchParams(window.location.search).get('token');

	useEffect(() => {
		if (token) {
			validateToken(token);
		} else {
			navigateWithLanguage('/login');
		}
	}, [token]);

	const handleLoginAfterReset = async (email: string) => {
		try {
			setIsLoading(true);
			const response = await login({
				email,
				password: formik.values.password,
			});

			if (response.error) {
				showSnackbar(t('error'), response.error, 'error');
				return;
			}

			if (!response.accessToken) {
				showSnackbar(t('error'), 'No access token received', 'error');
				return;
			}

			// Remove the reset password email cookie after successful login
			Cookies.remove('reset_password_email');

			// Check registration status
			const data = await checkStatus();
			const decodedToken = decodeToken(response.accessToken);

			await mutate('users', undefined, {
				revalidate: false,
				populateCache: true,
			});

			if (!data?.user_completed_registration) {
				navigateWithLanguage(data.nextTab, {
					replace: true,
				});
				return;
			}

			// Navigate to dashboard based on role
			if (
				decodedToken?.role.toLowerCase() === RoleEnum.Specialist ||
				decodedToken?.role.toLowerCase() === RoleEnum.Company
			) {
				navigateWithLanguage('/specialist-switcher', {
					replace: true,
				});
			} else {
				navigateWithLanguage(`/${decodedToken?.role.toLowerCase()}/dashboard`, {
					replace: true,
				});
			}
		} catch (error) {
			showSnackbar(t('error'), t('loginAfterResetFailed'), 'error');
			navigateWithLanguage('/login');
		} finally {
			setIsLoading(false);
		}
	};

	const formik = useFormik<ResetValues>({
		initialValues: resetValues,
		validationSchema: passwordSchema,
		onSubmit: async (values) => {
			if (!token) {
				return;
			}
			setIsLoading(true);
			const result = await resetPasswordWithToken(values.password, token);

			if (result.success) {
				setResetSuccess(true);
				// Get email from cookies
				const email = Cookies.get('reset_password_email');
				if (email) {
					await handleLoginAfterReset(email);
					return;
				}
				// Fallback if email not in cookies
				showSnackbar(t('success'), t('passwordResetSuccessful'), 'success');
				// Remove cookie even in fallback case
				Cookies.remove('reset_password_email');
				setTimeout(() => {
					navigateWithLanguage('/login');
				}, 1500);
			} else {
				setIsLoading(false);
				showSnackbar(t('error'), result.error || t('failedToResetPassword'), 'error');
			}
		},
	});

	const { values, touched, errors, handleChange, handleSubmit } = formik;

	const renderError = useMemo(() => {
		const hasPasswordError = touched.password && errors.password;
		const hasRepeatPasswordError = touched.repeatPassword && errors.repeatPassword;
		const errorText = errors.repeatPassword || errors.password;

		return (
			(hasRepeatPasswordError || hasPasswordError) && (
				<FormHelperText
					sx={{
						display: 'flex',
						marginTop: '10px',
						gap: '9px',
						marginBottom: '4px',
						color: '#FD3333',
						font: 'roboto-regular',
						fontSize: '16px',
					}}
				>
					<ErrorOutlineOutlinedIcon />
					{t(errorText || '')}
				</FormHelperText>
			)
		);
	}, [errors.repeatPassword, errors.password, t, touched.repeatPassword, touched.password]);

	// Loading state when validating token
	if (isValidating) {
		return (
			<AuthPepper className="bg-[#FFF8F4]">
				<div className="flex flex-col items-center justify-center h-[400px]">
					<CircularProgress />
				</div>
			</AuthPepper>
		);
	}

	// Error state
	if (!isValid) {
		return (
			<AuthPepper className="bg-[#FFF8F4]">
				<div className="max-w-xxl w-[330px] sm:w-[448px] max-sm:my-[35px] flex flex-col items-center justify-center h-[400px]">
					<ErrorOutlineOutlinedIcon color="error" sx={{ fontSize: 64 }} />
					<h2 className="text-xl mt-4 text-center mb-10">{t('invalidToken')}</h2>
					<Button
						variant="contained"
						onClick={() => handleNavigation('/login')}
						sx={{ font: 'roboto-bold', fontSize: '14px' }}
					>
						{t('goBackLogin')}
					</Button>
				</div>
			</AuthPepper>
		);
	}

	// Success state
	if (resetSuccess) {
		return (
			<AuthPepper className="bg-[#FFF8F4]">
				<div className="max-w-xxl w-[330px] sm:w-[448px] max-sm:my-[35px] flex flex-col items-center justify-center h-[400px]">
					<CircularProgress />
					<h2 className="text-xl font-bold mt-6">{t('successMessage')}</h2>
					<p className="text-gray-600 mt-2 text-center">{t('loggingYouIn')}</p>
				</div>
			</AuthPepper>
		);
	}

	return (
		<AuthPepper className="bg-[#FFF8F4]">
			<div className="max-w-xxl w-[330px] sm:w-[448px] max-sm:my-[35px]">
				<div className="mt-[50px]">
					<AuthHeader
						title={t('resetPassword')}
						spacing="mt-[50px] max-sm:mt-[122px]"
						wrapperSpacing="mb-[40px] max-sm:mb-[20px]"
					/>
				</div>
				<form
					className="max-w-custom-448 mx-auto mb-[80px] flex flex-col gap-[20px] pb-5"
					onSubmit={handleSubmit}
				>
					{/* Password Input Field */}
					<div>
						<TaInput
							icon={
								<LockOutlinedIcon
									sx={{
										color: touched.password && errors.password ? '#FD3333' : '#7B809A',
									}}
								/>
							}
							id="password"
							label={t('newPassword')}
							size="small"
							type="password"
							value={values.password}
							onChange={handleChange}
							error={errors.password}
							touched={touched.password}
						/>
						{((values.password && touched.password) || errors.password) && (
							<PasswordRequirements password={values.password} />
						)}
					</div>
					<div>
						<TaInput
							icon={
								<LockOutlinedIcon
									sx={{
										color: touched.repeatPassword && errors.repeatPassword ? '#FD3333' : '#7B809A',
									}}
								/>
							}
							id="repeatPassword"
							label={t('newPasswordRepeat')}
							size="small"
							type="password"
							value={values.repeatPassword}
							onChange={handleChange}
							error={errors.repeatPassword}
							touched={touched.repeatPassword}
						/>
						{touched.repeatPassword && errors.repeatPassword && (
							<FormHelperText
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '9px',
									marginBottom: '4px',
									color: errors.repeatPassword ? '#FD3333' : '#7B809A',
								}}
							>
								{renderError}
							</FormHelperText>
						)}
					</div>

					<Button
						type="submit"
						variant="contained"
						loading={isLoading}
						className="w-full mt-3 text-white"
						sx={{ font: 'roboto-bold', fontSize: '14px' }}
						disabled={isLoading}
					>
						{t('resetPassword')}
					</Button>
					<p
						onClick={() => handleNavigation('/login')}
						className="roboto-bold text-primary text-base underline flex justify-center cursor-pointer mt-[9px]"
					>
						{t('goBackLogin')}
					</p>
				</form>
			</div>
		</AuthPepper>
	);
};
